import { For, Show, createMemo, createSignal } from 'solid-js';
import type { ProjectManifest } from '../../types/project';
import { projectDisplayTitle } from '../../types/project';
import { PixelIcon } from '../PixelIcon';
import { ProjectIcon } from '../ProjectIcon';

interface ProjectWindowProps {
  project: ProjectManifest;
}

function buildSandbox(project: ProjectManifest): string {
  const tokens = ['allow-scripts', 'allow-same-origin', 'allow-forms'];
  const capabilities = project.capabilities || {};
  if (capabilities.downloads) tokens.push('allow-downloads');
  if (capabilities.pointerLock) tokens.push('allow-pointer-lock');
  if (capabilities.popups) tokens.push('allow-popups', 'allow-popups-to-escape-sandbox');
  return tokens.join(' ');
}

function buildAllow(project: ProjectManifest): string {
  const permissions: string[] = [];
  if (project.capabilities?.fullscreen !== false) permissions.push('fullscreen');
  if (project.capabilities?.clipboard) permissions.push('clipboard-read', 'clipboard-write');
  return permissions.join('; ');
}

function withReloadNonce(url: string, nonce: number): string {
  if (nonce === 0) return url;
  const separator = url.includes('?') ? '&' : '?';
  return `${url}${separator}workstation_reload=${nonce}`;
}

export function ProjectWindow(props: ProjectWindowProps) {
  const [reloadNonce, setReloadNonce] = createSignal(0);
  const [showDetails, setShowDetails] = createSignal(false);
  const [loaded, setLoaded] = createSignal(false);
  const runtimeUrl = createMemo(() => props.project.runtimeUrl || '#');
  const iframeUrl = createMemo(() => withReloadNonce(runtimeUrl(), reloadNonce()));
  const runtimeHost = createMemo(() => {
    try {
      return new URL(runtimeUrl()).host;
    } catch {
      return 'project runtime';
    }
  });
  let iframe: HTMLIFrameElement | undefined;

  async function enterFullscreen() {
    if (!iframe) return;
    try {
      await iframe.requestFullscreen();
    } catch {
      window.open(runtimeUrl(), '_blank', 'noopener,noreferrer');
    }
  }

  function reload() {
    setLoaded(false);
    setReloadNonce((value) => value + 1);
  }

  return (
    <div class="project-window">
      <div class="project-menu-bar" role="menubar" aria-label="Project menu">
        <button role="menuitem" onClick={() => setShowDetails(true)}><u>F</u>ile</button>
        <button role="menuitem" onClick={reload}><u>V</u>iew</button>
        <button role="menuitem" onClick={() => setShowDetails((value) => !value)}><u>P</u>roject</button>
        <button role="menuitem" onClick={() => setShowDetails(true)}><u>H</u>elp</button>
      </div>

      <div class="project-toolbar">
        <button onClick={() => setShowDetails((value) => !value)} title="About this project">
          <PixelIcon name="info" size={18} />
          <span>About</span>
        </button>
        <Show when={props.project.sourceUrl}>
          {(url) => (
            <button onClick={() => window.open(url(), '_blank', 'noopener,noreferrer')} title="Open source repository">
              <PixelIcon name="github" size={18} />
              <span>Source Code</span>
            </button>
          )}
        </Show>
        <button onClick={reload} title="Reload application">
          <span class="toolbar-glyph" aria-hidden="true">↻</span>
          <span>Reload</span>
        </button>
        <Show when={props.project.capabilities?.fullscreen !== false}>
          <button onClick={enterFullscreen} title="Enter full screen">
            <span class="toolbar-glyph" aria-hidden="true">□</span>
            <span>Full Screen</span>
          </button>
        </Show>
        <button onClick={() => window.open(runtimeUrl(), '_blank', 'noopener,noreferrer')} title="Open raw application in a new tab">
          <span class="toolbar-glyph" aria-hidden="true">↗</span>
          <span>Open App</span>
        </button>
        <div class="project-toolbar-spacer" />
        <span class="project-runtime-label">{runtimeHost()}</span>
      </div>

      <div class="project-stage">
        <Show when={!loaded()}>
          <div class="project-loading" aria-live="polite">
            <ProjectIcon project={props.project} size={44} />
            <div>
              <strong>Starting {projectDisplayTitle(props.project)}...</strong>
              <span>Loading the isolated project runtime</span>
            </div>
          </div>
        </Show>

        <iframe
          ref={iframe}
          class="project-iframe"
          src={iframeUrl()}
          title={props.project.title}
          sandbox={buildSandbox(props.project)}
          allow={buildAllow(props.project)}
          allowfullscreen={props.project.capabilities?.fullscreen !== false}
          loading="eager"
          onLoad={() => setLoaded(true)}
        />

        <Show when={showDetails()}>
          <aside class="project-about-panel window" aria-label="Project information">
            <div class="title-bar">
              <div class="title-bar-text">About {projectDisplayTitle(props.project)}</div>
              <div class="title-bar-controls">
                <button aria-label="Close" onClick={() => setShowDetails(false)} />
              </div>
            </div>
            <div class="window-body project-about-body">
              <div class="project-about-heading">
                <ProjectIcon project={props.project} size={42} />
                <div>
                  <h2>{props.project.title}</h2>
                  <small>{props.project.category}{props.project.year ? ` · ${props.project.year}` : ''}</small>
                </div>
              </div>
              <p>{props.project.description || props.project.summary}</p>
              <Show when={props.project.tags?.length}>
                <div class="tag-list">
                  <For each={props.project.tags}>{(tag) => <span>{tag}</span>}</For>
                </div>
              </Show>
              <dl class="project-properties">
                <div><dt>Status</dt><dd>{props.project.status}</dd></div>
                <div><dt>Runtime</dt><dd>Isolated iframe</dd></div>
                <div><dt>Source</dt><dd>{props.project.sourceUrl ? 'Public repository' : 'Not linked'}</dd></div>
              </dl>
              <div class="dialog-buttons">
                <button class="default" onClick={() => setShowDetails(false)}>OK</button>
              </div>
            </div>
          </aside>
        </Show>
      </div>

      <div class="status-bar project-status-bar">
        <p class="status-bar-field">{loaded() ? 'Ready' : 'Loading...'}</p>
        <p class="status-bar-field">Sandboxed project runtime</p>
        <p class="status-bar-field">{props.project.sourceUrl ? 'Open source' : 'Project'}</p>
      </div>
    </div>
  );
}
